import {
  Container,
  ContainerOptions,
  Graphics,
  Sprite,
  Text,
  TextStyle,
} from "pixi.js";
import gsap from "gsap";
import { DEFAULT_HISTORY } from "../StaticConfig";
import { ColumnAlignment } from "../GameConfig";
import i18next from "i18next";
import GlobalStore from "../services/GlobalStore";
import { CommonEvents } from "../events/EventEnums";
import { JackpotHitData } from "../models/JackpotHitData";
import { Marquee } from "./Marquee";
import { MarqueeJpHistoryItem } from "./MarqueeJpHistoryItem";

class JackpotHistory extends Container {
  private currentTimeline: gsap.core.Timeline | null = null;
  private fadeTimeline: gsap.core.Timeline | null = null;

  private titleValue: Text | undefined;
  private titleTime: Text | undefined;
  private textFieldsValue: Text[] = [];
  private textFieldsDate: Text[] = [];
  private textFieldsTimeOnly: Text[] = [];
  private textFieldsDateContainers: Container[] = [];
  private textFieldsLocation: Marquee[] = [];
  private textFieldsLocationText: Text[] = [];
  private textFieldsMachineIDText: Text[] = [];
  private textFieldsJackpotName: Text[] = [];
  private dividers: Sprite[] = [];
  private numEntriesMax: number = 10;
  private _numShowing: number = 0;

  private showTimeline: gsap.core.Timeline | null = null;
  private hideTimeline: gsap.core.Timeline | null = null;

  private showTime: boolean;

  private content: Container;
  private bgShade: Sprite;
  private bg: Sprite;
  private title: Sprite;
  private marquee: Marquee;
  private marqueeItems: MarqueeJpHistoryItem[] = [];
  private canShowMarquee: (() => boolean) | null = null;

  private expanded: boolean = false;
  private fadedOut: boolean = false;
  private locationMarqueeResumeDelay: gsap.core.Tween | null = null;

  private yTop = 0;
  private yBottom = 1080 - 225;

  constructor(
    showTime: boolean = false,
    canShowMarquee?: () => boolean,
    containerOptions?: ContainerOptions,
  ) {
    super(containerOptions);
    this.alpha = 0;

    this.bgShade = Sprite.from(`history-additional-bg`);
    this.bg = Sprite.from(`history-background`);
    this.title = Sprite.from(`history-title`);
    this.content = new Container();
    this.marquee = new Marquee({
      width: this.bg.width,
      align: "center",
    });
    this.updateMarquee();

    this.showTime = showTime;
    this.canShowMarquee = canShowMarquee ?? (() => true);

    const config = DEFAULT_HISTORY;
    this.numEntriesMax = config.numrows;

    console.log("History config:", config);

    this.setUpGraphics(config);

    if (this.expanded) {
      this.expand(true)?.totalProgress(1);
    } else {
      this.contract(true)?.totalProgress(1);
    }

    this.addEventListeners();

    // const plonk = Sprite.from(`_plonk_history`);
    // plonk.alpha = 0.2;
    // this.addChild(plonk);
  }

  private setUpGraphics = (config: any) => {
    const fontFamily: string = "Avenir Next Condensed Demi Bold";

    this.bgShade.anchor.set(0.5, 0);
    this.bgShade.position.set(1920 / 2, 0);

    this.bg.anchor.set(0.5, 0);
    this.bg.position.set(1920 / 2, 0);

    this.title.anchor.set(0.5, 0.5);
    this.title.position.set(-5, 67);

    this.addChild(this.content);
    this.content.addChild(this.bgShade, this.bg);
    this.marquee.position.set(-this.bg.width / 2, 120);
    this.bg.addChild(this.title, this.marquee);

    // EXTENDED DATA
    for (let i = 0; i < 9; i++) {
      const divider = Sprite.from(`history-line`);
      divider.anchor.set(0.5, 0.5);
      divider.x = 0;
      divider.y = 212 + 93 * i;
      divider.alpha = this.expanded ? 1 : 0;
      this.bg.addChild(divider);
      this.dividers.push(divider);
    }

    // Prepare positions and anchors for left and right aligned texts
    let value_anchorX = 0;
    if (config.rows.win.align == ColumnAlignment.RIGHT) {
      value_anchorX = 1;
    }

    let location_anchorX = 0;
    if (config.rows.location.align == ColumnAlignment.RIGHT) {
      location_anchorX = 1;
    }

    let time_anchorX = 0;
    if (config.rows.date.align == ColumnAlignment.RIGHT) {
      time_anchorX = 1;
    }

    // Setup titles
    if (config.header) {
      const headerColor: number = parseInt(config.header.color, 16);

      const titleStyleValue: TextStyle = new TextStyle({
        fontFamily: fontFamily,
        fontSize: config.header.font_size,
        fill: headerColor,
        align: config.rows.win.align,
      });

      const titleStyleTime: TextStyle = new TextStyle({
        fontFamily: fontFamily,
        fontSize: config.header.font_size,
        fill: headerColor,
        align: config.rows.date.align,
      });

      this.titleValue = new Text({
        text: i18next.t("common.title_value"),
        style: titleStyleValue,
      });
      this.titleValue.x = config.rows.win.x;
      this.titleValue.y = config.header.y;
      this.titleValue.anchor.set(value_anchorX, 0.5);
      this.titleValue.alpha = 0;
      this.addChild(this.titleValue);

      this.titleTime = new Text({
        text: i18next.t("common.title_time"),
        style: titleStyleTime,
      });
      this.titleTime.x = config.rows.date.x;
      this.titleTime.y = config.header.y;
      this.titleTime.anchor.set(time_anchorX, 0.5);
      this.titleTime.alpha = 0;
      this.addChild(this.titleTime);
    }

    for (let i = 0; i < this.numEntriesMax; i++) {
      console.log(
        "From config:",
        config.rows.start_y,
        i,
        config.rows.spacing_y,
        config.rows.start_y + i * config.rows.spacing_y,
      );

      const yRow = config.rows.start_y + i * config.rows.spacing_y;
      {
        // Win
        const { x, y, size, color, align } = config.rows.win;
        const text = new Text({
          text: "",
          style: {
            fontFamily: "Faustina Extrabold",
            fontSize: size,
            fill: color,
            align: align,
          },
        });

        text.alpha = 0;
        text.anchor.set(align === "right" ? 1 : 0, 1);
        text.x = x;
        text.y = yRow + y;
        this.content.addChild(text);

        this.textFieldsValue.push(text);
      }
      // Calculate column widths before creating elements
      const jpNameColumnWidth = 250; // Width for jackpot name column
      const winTextReservedWidth = 350; // Width for win/jackpot values
      const padding = 40; // Padding between columns
      const totalAvailableSpace = config.rows.win.x - config.rows.location.x;
      const locationWidth =
        totalAvailableSpace -
        jpNameColumnWidth -
        winTextReservedWidth -
        padding;
      const showMachId = GlobalStore.generalSettings.showMachineIdInHistory;

      {
        // Location - using Marquee for scrolling machine info
        const { x, y, size, color, align } = config.rows.location;

        const locationText = new Text({
          text: "",
          style: {
            fontFamily: "Avenir Next Condensed Demi Bold",
            fontSize: size,
            fill: color,
            align: align,
          },
        });
        locationText.anchor.set(0, 1); // bottom-left, matching row baseline

        const locationField = new Marquee({
          width: locationWidth,
          speed: 120, // ~2 pixels per frame at 60 fps
          gap: 0,
          mode: "sway",
          swayDelay: 2000,
        });

        // Clip to column width so text doesn't bleed into adjacent columns
        const locationMask = new Graphics();
        locationMask
          .rect(0, -(size + 20), locationWidth, size + 20)
          .fill(0xffffff);
        locationField.addChild(locationMask);
        locationField.mask = locationMask;

        locationField.pause();
        locationField.alpha = 0;
        locationField.x = x;
        locationField.y = yRow + y + (showMachId ? -8 : 0);
        this.content.addChild(locationField);

        this.textFieldsLocation.push(locationField);
        this.textFieldsLocationText.push(locationText);
      }
      {
        // Machine ID — static label shown below the location marquee
        const { x, y, size, color } = config.rows.location;
        const machineIdSize = 18;

        const machineIdText = new Text({
          text: "",
          style: {
            fontFamily: "Avenir Next Condensed Demi Bold",
            fontSize: machineIdSize,
            fill: color,
            align: "left",
          },
        });

        machineIdText.alpha = 0;
        machineIdText.anchor.set(0, 0); // top-left so it hangs below the marquee baseline
        machineIdText.x = x + 2;
        machineIdText.y = yRow + y - 11; // 3px below the location text baseline
        this.content.addChild(machineIdText);

        this.textFieldsMachineIDText.push(machineIdText);
      }
      {
        // Jackpot Name - new column between location and win
        // Position it after the location marquee
        const jpNameX = config.rows.location.x + locationWidth + 20; // 20px padding after location
        const jpNameSize = 45;
        const jpNameColor = 0xf2e476;

        const jpNameText = new Text({
          text: "",
          style: {
            fontFamily: "Avenir Next Condensed Demi Bold",
            fontSize: jpNameSize,
            fill: jpNameColor,
            align: "left",
          },
        });

        jpNameText.alpha = 0;
        jpNameText.anchor.set(0, 1); // Bottom-aligned like other text
        jpNameText.x = jpNameX;
        jpNameText.y = yRow + config.rows.location.y; // Same y as location
        this.content.addChild(jpNameText);

        this.textFieldsJackpotName.push(jpNameText);
        console.log("Jackpot name text setup:", jpNameText.x, jpNameText.y);
      }
      {
        // Date & Time — two separate centered texts inside a container, vertically centred in the row
        const { x, y, size, color } = config.rows.date;
        const colWidth = config.rows.location.x - x;
        const timeSize = Math.floor(size * 0.8);
        const gap = 6; // px between date bottom and time top

        const dateContainer = new Container();
        dateContainer.alpha = 0;
        dateContainer.x = x;
        // Centre the whole block in the row
        dateContainer.y = yRow - config.rows.spacing_y / 2 + y;
        this.content.addChild(dateContainer);
        this.textFieldsDateContainers.push(dateContainer);

        const baseStyle = {
          fontFamily: "Avenir Next Condensed Demi Bold",
          fill: color,
          align: "center" as const,
        };

        const dateText = new Text({
          text: "",
          style: { ...baseStyle, fontSize: size },
        });
        dateText.anchor.set(0.5, 0.5);
        dateText.x = colWidth / 2;
        // When showing time: date sits in the upper half, centred above the gap
        dateText.y = this.showTime ? -(gap / 2 + timeSize / 2) : 0;
        dateContainer.addChild(dateText);
        this.textFieldsDate.push(dateText);

        const timeText = new Text({
          text: "",
          style: { ...baseStyle, fontSize: timeSize },
        });
        timeText.anchor.set(0.5, 0.5);
        timeText.x = colWidth / 2;
        // Time sits in the lower half, centred below the gap
        timeText.y = gap / 2 + size / 2;
        timeText.visible = this.showTime;
        dateContainer.addChild(timeText);
        this.textFieldsTimeOnly.push(timeText);
      }
    }
  };

  public update = (history: Array<JackpotHitData>): void => {
    history.splice(this.numEntriesMax);

    const formatCurrency = GlobalStore.formatCurrency;
    const dateLocale = GlobalStore.getDateLocale();

    this._numShowing = 0;
    for (let i = 0; i < this.numEntriesMax; i++) {
      if (i < history.length) {
        this.textFieldsValue[i].text = `${formatCurrency(history[i].value)}`;

        // Build location text based on settings
        const locText = this.textFieldsLocationText[i];
        locText.text = history[i].location;
        this.textFieldsLocation[i].clear();
        this.textFieldsLocation[i].addItem(locText);
        this.textFieldsMachineIDText[i].text = GlobalStore.generalSettings
          .showMachineIdInHistory
          ? `ID: ${history[i].machineId}`
          : "";

        // Set jackpot name in its own column
        // this.textFieldsJackpotName[i].text = GlobalStore.generalSettings
        //   .showJackpotNameInHistory
        //   ? history[i].name
        //   : "";
        this.textFieldsDate[i].text =
          history[i].timestamp.toLocaleDateString(dateLocale);
        this.textFieldsTimeOnly[i].text = this.showTime
          ? history[i].timestamp.toLocaleTimeString(dateLocale, {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            })
          : "";

        this._numShowing += 1;
      } else {
        this.textFieldsValue[i].text = "";
        this.textFieldsLocationText[i].text = "";
        this.textFieldsMachineIDText[i].text = "";
        this.textFieldsLocation[i].clear();
        this.textFieldsJackpotName[i].text = "";
        this.textFieldsDate[i].text = "";
        this.textFieldsTimeOnly[i].text = "";
      }

      console.log(this.textFieldsValue[i].text, history[i]);
    }

    if (this._numShowing === 0) this.fadeOut();

    this.updateMarquee();
  };

  private updateMarquee = () => {
    const hist = GlobalStore.getJackpotHistory();

    if (!hist.length) {
      return;
    }
    hist
      .sort((a, b) => {
        return b.timestamp.getTime() - a.timestamp.getTime();
      })
      .splice(this.numEntriesMax);

    let i: number = 0;
    for (const item of hist) {
      if (this.marqueeItems.length > i) {
        this.marqueeItems[i].setData(item);
      } else {
        this.marqueeItems.push(
          new MarqueeJpHistoryItem({
            jpHitData: item,
            showTime: this.showTime,
          }),
        );
      }

      i++;
    }

    this.marquee.clear();
    for (let j = 0; j < i; j++) {
      this.marquee.addItem(this.marqueeItems[j], { updateLayout: j == i - 1 });
    }
    this.marquee.pause();
  };

  public show = (): void => {
    this.visible = true;
    this.alpha = 1;
    if (this._numShowing === 0) this.content.alpha = 0;
  };

  public hide = (): void => {
    this.visible = false;
    this.alpha = 0;
  };

  public hideInstant = (): void => {
    this.visible = false;
    this.showTimeline?.pause(0);
    this.hideTimeline?.pause(0);

    for (const text of [
      ...this.textFieldsDateContainers,
      ...this.textFieldsLocation,
      ...this.textFieldsValue,
      ...this.dividers,
    ]) {
      text.alpha = 0;
    }
  };

  private addEventListeners = () => {
    const commonEE = GlobalStore.commonEventEmitter;

    commonEE.on(CommonEvents.CHANGE_LANGUAGE, this.updateLanguage);
  };

  private removeEventListeners = () => {
    const commonEE = GlobalStore.commonEventEmitter;

    commonEE.off(CommonEvents.CHANGE_LANGUAGE, this.updateLanguage);
  };

  private pauseLocationMarquees = (): void => {
    this.locationMarqueeResumeDelay?.kill();
    this.locationMarqueeResumeDelay = null;
    for (const marquee of this.textFieldsLocation) {
      marquee.pause();
    }
  };

  private resumeLocationMarquees = (): void => {
    this.locationMarqueeResumeDelay?.kill();
    for (const marquee of this.textFieldsLocation) {
      marquee.reset();
    }
    this.locationMarqueeResumeDelay = gsap.delayedCall(3, () => {
      this.locationMarqueeResumeDelay = null;
      for (const marquee of this.textFieldsLocation) {
        marquee.resume();
      }
    });
  };

  private updateLanguage = (): void => {
    if (this.titleValue) this.titleValue.text = i18next.t("common.title_value");

    if (this.titleTime) this.titleTime.text = i18next.t("common.title_time");
  };

  public expand = (force?: boolean): gsap.core.Animation | null => {
    if (this.expanded && !force) return null;

    const tl = gsap.timeline({
      repeatRefresh: true,
      onStart: () => {
        this.expanded = true;
        this.resumeLocationMarquees();
        if (this.currentTimeline) {
          this.currentTimeline.kill();
        }
        this.currentTimeline = tl;
      },
      onComplete: () => {
        if (this.currentTimeline === tl) {
          this.currentTimeline.kill();
          this.currentTimeline = null;
        }
      },
    });

    const optsStart = { pixi: { alpha: 0 } };
    const opts = {
      duration: 0.5,
      stagger: 0,
      delay: 0,
      pixi: {
        alpha: 1,
      },
    };
    tl.addLabel("start")
      .set(
        [
          this.textFieldsLocation,
          this.textFieldsMachineIDText,
          this.textFieldsDateContainers,
          this.textFieldsValue,
          this.dividers,
        ],
        {
          pixi: {
            alpha: 0,
          },
        },
      )
      .to(this.marquee, {
        pixi: {
          alpha: 0,
        },
        onComplete: () => {
          this.marquee.pause();
        },
      })
      .to(this.content, {
        y: this.yTop,
        ease: "sine.inOut",
        duration: 1,
      })
      .to(
        this.title,
        {
          pixi: {
            alpha: 1,
          },
          duration: 1,
        },
        "<",
      )
      .add([
        gsap.fromTo(
          [
            this.textFieldsLocation,
            this.textFieldsMachineIDText,
            this.textFieldsValue,
            this.textFieldsDateContainers,
            this.dividers,
          ],
          optsStart,
          opts,
        ),
      ]);

    return tl;
  };

  public contract = (force?: boolean): gsap.core.Animation | null => {
    if (!this.expanded && !force) return null;

    const optsStart = {
      pixi: {
        alpha: 1,
      },
    };

    const opts = {
      duration: 0.5,
      stagger: 0,
      delay: 0, //Math.max(0, 0.5 - 0.5),
      pixi: {
        alpha: 0,
      },
    };

    const tl = gsap.timeline({
      repeatRefresh: true,
      onStart: () => {
        this.expanded = false;
        this.pauseLocationMarquees();
        if (this.currentTimeline) {
          this.currentTimeline.kill();
        }
        this.currentTimeline = tl;
      },
      onComplete: () => {
        if (this.currentTimeline === tl) {
          this.currentTimeline.kill();
          this.currentTimeline = null;
        }
      },
    });

    tl.addLabel("start")
      .fromTo(
        [
          this.textFieldsLocation,
          this.textFieldsMachineIDText,
          this.textFieldsValue,
          this.textFieldsDateContainers,
          this.dividers,
        ],
        optsStart,
        opts,
      )
      .to(
        this.content,
        {
          y: this.yBottom,
          ease: "sine.inOut",
          duration: 1,
        },
        "start",
      )
      .to(
        this.title,
        {
          pixi: {
            alpha: 0,
          },
          duration: 1,
        },
        "<",
      )
      .to(this.marquee, {
        pixi: {
          alpha: this.canShowMarquee?.() ? 1 : 0,
        },
        onStart: () => {
          if (this.canShowMarquee?.()) {
            this.marquee.resume();
          }
        },
      });

    return tl;
  };

  public fadeIn = (): gsap.core.Animation | null => {
    if (!this.fadedOut || this._numShowing === 0) return null;

    const tl = gsap.timeline({
      repeatRefresh: true,
      onStart: () => {
        this.fadedOut = false;
        if (this.fadeTimeline) {
          this.fadeTimeline.kill();
        }
        this.fadeTimeline = tl;
      },
      onComplete: () => {
        if (this.fadeTimeline === tl) {
          this.fadeTimeline.kill();
          this.fadeTimeline = null;
        }
      },
    });

    tl.addLabel("start").to(
      this.content,
      {
        alpha: 1,
        ease: "sine.out",
        duration: 0.2,
        onStart: () => {
          if (!this.expanded && this.canShowMarquee?.()) this.marquee.resume();
          if (this.expanded) this.resumeLocationMarquees();
        },
      },
      "start",
    );

    return tl;
  };

  public fadeOut = (): gsap.core.Animation | null => {
    if (this.fadedOut) return null;

    const tl = gsap.timeline({
      repeatRefresh: true,
      onStart: () => {
        this.fadedOut = true;
        if (this.fadeTimeline) {
          this.fadeTimeline.kill();
        }
        this.fadeTimeline = tl;
      },
      onComplete: () => {
        if (this.fadeTimeline === tl) {
          this.fadeTimeline.kill();
          this.fadeTimeline = null;
        }
      },
    });

    tl.addLabel("start").to(
      this.content,
      {
        alpha: 0,
        ease: "sine.in",
        duration: 0.2,
        onComplete: () => {
          this.marquee.pause();
          this.pauseLocationMarquees();
        },
      },
      "start",
    );

    return tl;
  };

  public get numEntries(): number {
    return this._numShowing;
  }

  public destroy(): void {
    this.locationMarqueeResumeDelay?.kill();
    this.locationMarqueeResumeDelay = null;
    this.removeEventListeners();

    // Clean up timelines
    if (this.currentTimeline) {
      this.currentTimeline.kill();
      this.currentTimeline = null;
    }
    if (this.fadeTimeline) {
      this.fadeTimeline.kill();
      this.fadeTimeline = null;
    }
    if (this.showTimeline) {
      this.showTimeline.kill();
      this.showTimeline = null;
    }
    if (this.hideTimeline) {
      this.hideTimeline.kill();
      this.hideTimeline = null;
    }
  }
}

export default JackpotHistory;
